import { useMutation, useQuery } from '@tanstack/react-query'
import { PartyPopper } from 'lucide-react'
import { parseCookies, setCookie } from 'nookies'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { getCampaigns } from '@/api/campaigns/get-campaigns'
import { getPaymentByCampaignId } from '@/api/payments/get-payment'
import { getProfile } from '@/api/user/get-profile'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { env } from '@/env'
import { useHeadManager } from '@/lib/headManagerProvider'

import { AwardedCampaignModal } from './components/awardedCampaignModal'
import { CampaignDetailsModal } from './components/campaignDetailsModal'
import { NewCampaingnsModal } from './components/newCampaingns'
import { GetCampaigns } from './types'

export function Campaigns() {
  const { setTitle } = useHeadManager()
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [awardedOpen, setAwardedOpen] = useState(false)
  const cookies = parseCookies()
  const { 'reactAuth.email': email, 'reactAuth.id': userId } = cookies

  const [selectedRifa, setSelectedRifa] = useState<GetCampaigns | null>(null)
  const [selectedAwarded, setSelectedAwarded] = useState<GetCampaigns | null>(
    null,
  )

  const [campanhasParticipando, setCampanhasParticipando] = useState<
    GetCampaigns[]
  >([])
  const [campanhasMinhas, setCampanhasMinhas] = useState<GetCampaigns[]>([])
  const [campanhasAbertas, setCampanhasAbertas] = useState<GetCampaigns[]>([])
  const [campanhasEncerradas, setCampanhasEncerradas] = useState<
    GetCampaigns[]
  >([])

  const scrollMinhasRef = useRef<HTMLDivElement | null>(null)
  const scrollPremiadasRef = useRef<HTMLDivElement | null>(null)
  const scrollParticipandoRef = useRef<HTMLDivElement | null>(null)

  function scrollLeftMinhas() {
    if (scrollMinhasRef.current) {
      scrollMinhasRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }

  function scrollRightMinhas() {
    if (scrollMinhasRef.current) {
      scrollMinhasRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }

  function scrollLeftPremiadas() {
    if (scrollPremiadasRef.current) {
      scrollPremiadasRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }

  function scrollRightPremiadas() {
    if (scrollPremiadasRef.current) {
      scrollPremiadasRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }

  function scrollLeftParticipando() {
    if (scrollParticipandoRef.current) {
      scrollParticipandoRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }

  function scrollRightParticipando() {
    if (scrollParticipandoRef.current) {
      scrollParticipandoRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }

  const getAllCampaigns = useMutation({
    mutationFn: getCampaigns,
    onSuccess: (data) => {
      setCampanhasAbertas(
        data.filter(
          (campanha) =>
            campanha.campanha.sorteado === false &&
            campanha.campanha.status === true,
        ),
      )

      setCampanhasEncerradas(
        data.filter((campanha) => campanha.campanha.sorteado === true),
      )

      setCampanhasMinhas(
        data.filter(
          (campanha) => campanha.campanha.usuario_Id === Number(userId),
        ),
      )

      const fetchPayments = async () => {
        const payments = await Promise.all(
          data.map(async (campanha) => {
            try {
              const paymentData = await getPaymentByCampaignId(
                campanha.campanha.id,
              )
              return { campanha, paymentData }
            } catch (error) {
              console.error(
                'Erro ao buscar pagamento para campanha',
                campanha.campanha.id,
                error,
              )
              return { campanha, paymentData: null }
            }
          }),
        )

        const campanhasComPagamento = payments.filter(
          ({ paymentData }) =>
            paymentData &&
            Array.isArray(paymentData) &&
            paymentData.some((item) => item.usuarioId === Number(userId)),
        )

        setCampanhasParticipando(
          campanhasComPagamento.map(({ campanha }) => campanha),
        )
      }

      fetchPayments()
    },
    onError: (error) => {
      console.error('Erro ao buscar campanhas:', error)
    },
  })

  useEffect(() => {
    setTitle('Campanhas')
    getAllCampaigns.mutate()
  }, [setTitle])

  const openAwarded = (campaign: GetCampaigns) => {
    setSelectedAwarded(campaign)
    setAwardedOpen(true)
  }

  const openDetails = (campaign: GetCampaigns) => {
    setSelectedRifa(campaign)
    setDetailsOpen(true)
  }

  const { data: profile } = useQuery({
    queryKey: ['profile', email],
    queryFn: () => getProfile({ email }),
    staleTime: Infinity,
  })

  const handleCreateCampaignClick = () => {
    if (!userId) {
      setLoginModalOpen(true)
    } else {
      setModalOpen(true)
    }
  }

  const handleRedirect = (path: string) => {
    const currentPath = location.pathname + location.search
    setCookie(null, 'redirectAfterLogin', currentPath, {
      expires: new Date('2238-01-01'),
      path: '/',
    })
    window.location.href = path
  }

  const campanhasParticipandoFiltradas = useMemo(() => {
    return campanhasParticipando.filter((rifa) =>
      rifa.campanha.titulo.toLowerCase().includes(search.toLowerCase()),
    )
  }, [campanhasParticipando, search])

  const campanhasMinhasFiltradas = useMemo(() => {
    return campanhasMinhas.filter((rifa) =>
      rifa.campanha.titulo.toLowerCase().includes(search.toLowerCase()),
    )
  }, [campanhasMinhas, search])

  const campanhasAbertasFiltradas = useMemo(() => {
    return campanhasAbertas.filter((rifa) =>
      rifa.campanha.titulo.toLowerCase().includes(search.toLowerCase()),
    )
  }, [campanhasEncerradas, search])

  const campanhasEncerradasFiltradas = useMemo(() => {
    return campanhasEncerradas.filter((rifa) =>
      rifa.campanha.titulo.toLowerCase().includes(search.toLowerCase()),
    )
  }, [campanhasEncerradas, search])

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          type="text"
          placeholder="Buscar campanhas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:flex-1"
        />
        {profile?.role === 'Admin' && (
          <Button
            className="w-full sm:w-auto"
            onClick={handleCreateCampaignClick}
          >
            Criar Campanha
          </Button>
        )}
      </div>

      {/* Campanhas Participando */}
      {campanhasParticipandoFiltradas.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Campanhas Participando</h2>
            <button
              onClick={() => navigate('/campanhas/participando')}
              className="text-primary text-sm font-medium underline hover:opacity-80"
            >
              Ver histórico
            </button>
          </div>

          <div className="relative">
            <button
              onClick={scrollLeftParticipando}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 left-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              ←
            </button>
            <div
              ref={scrollParticipandoRef}
              className="no-scrollbar flex gap-4 overflow-x-auto px-8 py-2"
            >
              {campanhasParticipandoFiltradas.map((rifa) => (
                <Card
                  key={`participando-${rifa.campanha.id}`}
                  className="max-w-[280px] min-w-[280px] flex-shrink-0 cursor-pointer rounded-2xl shadow-md"
                  onClick={() =>
                    navigate(`/campanhas/${rifa.campanha.id}/participando`)
                  }
                >
                  <img
                    src={`${env.VITE_API_URL}${rifa.campanha.imagem}`}
                    alt={rifa.campanha.titulo}
                    className="h-32 w-full object-cover"
                  />
                  <CardContent className="space-y-1 p-3">
                    <h3 className="text-base font-semibold">
                      {rifa.campanha.titulo}
                    </h3>
                    <p className="text-muted-foreground line-clamp-2 text-sm">
                      {rifa.campanha.descricao}
                    </p>
                    <span className="text-primary block font-bold">
                      R$ {Number(rifa.campanha.titulo_Valor).toFixed(2)}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
            <button
              onClick={scrollRightParticipando}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 right-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Minhas Campanhas */}
      {campanhasMinhasFiltradas.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Minhas Campanhas</h2>
            <button
              onClick={() => navigate('/campanhas/minhas')}
              className="text-primary text-sm font-medium underline hover:opacity-80"
            >
              Ver histórico
            </button>
          </div>

          <div className="relative">
            <button
              onClick={scrollLeftMinhas}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 left-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              ←
            </button>
            <div
              ref={scrollMinhasRef}
              className="no-scrollbar flex gap-4 overflow-x-auto px-8 py-2"
            >
              {campanhasMinhasFiltradas.map((rifa) => (
                <Card
                  key={`minhas-${rifa.campanha.id}`}
                  className="max-w-[280px] min-w-[280px] flex-shrink-0 cursor-pointer rounded-2xl shadow-md"
                  onClick={() =>
                    navigate(`/campanhas/${rifa.campanha.id}/gerenciamento`)
                  }
                >
                  <img
                    src={`${env.VITE_API_URL}${rifa.campanha.imagem}`}
                    alt={rifa.campanha.titulo}
                    className="h-32 w-full object-cover"
                  />
                  <CardContent className="space-y-1 p-3">
                    <h3 className="text-base font-semibold">
                      {rifa.campanha.titulo}
                    </h3>
                    <p className="text-muted-foreground line-clamp-2 text-sm">
                      {rifa.campanha.descricao}
                    </p>
                    <span className="text-primary block font-bold">
                      R$ {Number(rifa.campanha.titulo_Valor).toFixed(2)}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
            <button
              onClick={scrollRightMinhas}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 right-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Campanhas Premiadas */}
      {campanhasEncerradasFiltradas.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Campanhas Premiadas</h2>
            <button
              onClick={() => navigate('/campanhas/premiadas')}
              className="text-primary text-sm font-medium underline hover:opacity-80"
            >
              Ver todas
            </button>
          </div>

          <div className="relative">
            <button
              onClick={scrollLeftPremiadas}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 left-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              ←
            </button>
            <div
              ref={scrollPremiadasRef}
              className="no-scrollbar flex gap-4 overflow-x-auto px-8 py-2"
            >
              {campanhasEncerradasFiltradas.map((rifa) => (
                <Card
                  key={`premiada-${rifa.campanha.id}`}
                  onClick={() => openAwarded(rifa)}
                  className="hover:ring-primary max-w-[280px] min-w-[280px] flex-shrink-0 cursor-pointer overflow-hidden rounded-2xl shadow-md transition hover:ring-2"
                >
                  <div className="relative">
                    <img
                      src={`${env.VITE_API_URL}${rifa.campanha.imagem}`}
                      alt={rifa.campanha.titulo}
                      className="h-32 w-full object-cover brightness-75"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PartyPopper className="h-[50%] w-[50%] text-white opacity-90 drop-shadow-lg" />
                    </div>
                    <div className="absolute bottom-1 left-2 text-xs text-white">
                      Clique para ver o ganhador
                    </div>
                  </div>
                  <CardContent className="space-y-1 p-3">
                    <h3 className="text-base font-semibold">
                      {rifa.campanha.titulo}
                    </h3>
                    <p className="text-muted-foreground line-clamp-2 text-sm">
                      {rifa.campanha.descricao}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <button
              onClick={scrollRightPremiadas}
              className="bg-background text-foreground hover:bg-muted absolute top-1/2 right-0 z-10 hidden -translate-y-1/2 rounded-full p-2 shadow-md transition-colors sm:block"
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Todas as Campanhas */}
      <div className="space-y-8 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Todas as Campanhas</h2>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {campanhasAbertasFiltradas.map((rifa) => (
            <Card
              key={`todas-${rifa.campanha.id}`}
              className="relative flex h-full flex-col rounded-2xl shadow-md"
            >
              <img
                src={`${env.VITE_API_URL}${rifa.campanha.imagem}`}
                alt={rifa.campanha.titulo}
                className="h-48 w-full object-cover"
              />
              <CardContent className="flex flex-1 flex-col justify-between p-4">
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">
                    {rifa.campanha.titulo}
                  </h3>
                  <p className="text-muted-foreground line-clamp-2 text-sm">
                    {rifa.campanha.descricao}
                  </p>
                  <span className="text-primary block font-bold">
                    R$ {Number(rifa.campanha.titulo_Valor).toFixed(2)}
                  </span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => openDetails(rifa)}
                  >
                    Saiba mais
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() =>
                      navigate(`/campanhas/${rifa.campanha.id}/checkout`)
                    }
                  >
                    Participar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Modals */}
      <NewCampaingnsModal open={modalOpen} onOpenChange={setModalOpen} />
      <CampaignDetailsModal
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        rifa={selectedRifa}
      />
      <AwardedCampaignModal
        open={awardedOpen}
        onOpenChange={setAwardedOpen}
        campaign={selectedAwarded}
      />

      {/* Modal de Login */}
      <Dialog open={loginModalOpen} onOpenChange={setLoginModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-semibold">
              Acesse sua conta
            </DialogTitle>
            <DialogDescription className="mt-2 text-center text-gray-500">
              Para criar campanhas, você precisa estar logado. Se ainda não tem
              uma conta, pode criar uma agora mesmo!
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex justify-center gap-4">
            <Button onClick={() => handleRedirect('/sign-in')} className="w-32">
              Fazer login
            </Button>
            <Button
              variant="outline"
              onClick={() => handleRedirect('/sign-up')}
              className="w-32 border-blue-600 text-blue-600 hover:bg-blue-100"
            >
              Criar conta
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
